/**
 * @file httpService.js
 * @description Sets up Axios instance with global request and response configurations.
 * Use "http" for general API requests.
 * Use "authHttp" for anything related to authentication.
 * The endpoints for these APIs will eventually be consolidated once the migration from "Go" to ".net" happens.
 */

/**
 * @typedef {import('axios').AxiosInstance} AxiosInstance
 */
import axios from 'axios';
// import { DataMocking } from "./dataMockingService.js";

// eslint-disable-next-line no-unused-vars
let logoutFunction = null;

/**
 * Sets up interceptors for all authenticated instances.
 * These instances are used for API requests that require authentication (Bearer JWT Token).
 */
const setupInterceptors = () => {
    const authInstances = [
        http,
        importHttp,
        popGroupHttp,
        contentHttp,
        fileUploadHttp,
        systemHttp,
        clientApiHttp,
        individualApiHttp,
        userApiHttp,
        agencyApiHttp,
        authHttpWithAuthorization,
        loggingApiHttp,
    ];

    authInstances.forEach(instance => {
        instance.interceptors.request.use(
            async config => {
                config = addTenantHeader(config);
                return addAuthHeaders(config);
            },
            error => Promise.reject(error),
        );

        instance.interceptors.response.use(
            response => {
                return response;
            },
            error => {
                return Promise.reject(error);
            },
        );
    });
};

/**
 * Axios instance for general API requests.
 * Sets up a base URL and common headers.
 * @type {AxiosInstance}
 */
export const http = axios.create({
    baseURL: process.env.REACT_APP_API_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

/**
 * Axios instance for authentication-related API requests.
 * Sets up a base URL and common headers.
 * @type {AxiosInstance}
 */
export const authHttp = axios.create({
    baseURL:
        process.env.REACT_APP_RUNNING_BACKEND_LOCALLY === 'true'
            ? process.env.REACT_APP_AUTH_API_URL
            : process.env.REACT_APP_API_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

/**
 * Axios instance for authentication-related API requests.
 * Sets up a base URL and common headers.
 * @type {AxiosInstance}
 */
export const authHttpWithAuthorization = axios.create({
    baseURL:
        process.env.REACT_APP_RUNNING_BACKEND_LOCALLY === 'true'
            ? process.env.REACT_APP_AUTH_API_URL
            : process.env.REACT_APP_API_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

/**
 * Axios instance for import-related API requests.
 * @type {AxiosInstance}
 */
export const importHttp = axios.create({
    baseURL:
        process.env.REACT_APP_RUNNING_BACKEND_LOCALLY === 'true'
            ? process.env.REACT_APP_IMPORT_API_URL
            : process.env.REACT_APP_API_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

/**
 * Axios instance for pop group-related API requests.
 * @type {AxiosInstance}
 */
export const popGroupHttp = axios.create({
    baseURL:
        process.env.REACT_APP_RUNNING_BACKEND_LOCALLY === 'true'
            ? process.env.REACT_APP_POP_GROUP_API_URL
            : process.env.REACT_APP_API_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

/**
 * Axios instance for content API requests.
 * @type {AxiosInstance}
 */
export const contentHttp = axios.create({
    baseURL:
        process.env.REACT_APP_RUNNING_BACKEND_LOCALLY === 'true'
            ? process.env.REACT_APP_CONTENT_API_URL
            : process.env.REACT_APP_API_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

export const fileUploadHttp = axios.create({
    baseURL:
        process.env.REACT_APP_RUNNING_BACKEND_LOCALLY === 'true'
            ? process.env.REACT_APP_CONTENT_API_URL
            : process.env.REACT_APP_API_URL,
    headers: {
        Accept: 'application/json',
    },
});

/**
 * Axios instance for system API requests.
 * @type {AxiosInstance}
 */
export const systemHttp = axios.create({
    baseURL:
        process.env.REACT_APP_RUNNING_BACKEND_LOCALLY === 'true'
            ? process.env.REACT_APP_SYSTEM_API_URL
            : process.env.REACT_APP_API_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

/**
 * Axios instance for client API requests.
 * @type {AxiosInstance}
 */
export const clientApiHttp = axios.create({
    baseURL:
        process.env.REACT_APP_RUNNING_BACKEND_LOCALLY === 'true'
            ? process.env.REACT_APP_CLIENT_API_URL
            : process.env.REACT_APP_API_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

/**
 * Unauthenticated Axios instance for public client API requests (no JWT required).
 * @type {AxiosInstance}
 */
export const publicClientApiHttp = axios.create({
    baseURL:
        process.env.REACT_APP_RUNNING_BACKEND_LOCALLY === 'true'
            ? process.env.REACT_APP_CLIENT_API_URL
            : process.env.REACT_APP_API_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

/**
 * Axios instance for logging API requests.
 * @type {AxiosInstance}
 */
export const loggingApiHttp = axios.create({
    baseURL:
        process.env.REACT_APP_RUNNING_BACKEND_LOCALLY === 'true'
            ? process.env.REACT_APP_LOGGING_API_URL
            : process.env.REACT_APP_API_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

/**
 * Axios instance for individual API requests.
 * @type {AxiosInstance}
 */
export const individualApiHttp = axios.create({
    baseURL:
        process.env.REACT_APP_RUNNING_BACKEND_LOCALLY === 'true'
            ? process.env.REACT_APP_INDIVIDUAL_API_URL
            : process.env.REACT_APP_API_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

/**
 * Axios instance for User API requests.
 * @type {AxiosInstance}
 */
export const userApiHttp = axios.create({
    baseURL:
        process.env.REACT_APP_RUNNING_BACKEND_LOCALLY === 'true'
            ? process.env.REACT_APP_USER_API_URL
            : process.env.REACT_APP_API_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

/**
 * Axios instance for agency API requests.
 * @type {AxiosInstance}
 */
export const agencyApiHttp = axios.create({
    baseURL: process.env.REACT_APP_API_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

/**
 * Adds X-Tenant-Namespace header to the request config.
 * @param {Object} config - The Axios request configuration.
 * @returns {Object} The updated Axios request configuration.
 */
const addTenantHeader = config => {
    const companyCode = window.localStorage.getItem('companyCode') || null;
    if (
        companyCode &&
        !config.headers['X-Tenant-Namespace'] &&
        !config.skipTenantHeader
    ) {
        config.headers['X-Tenant-Namespace'] = companyCode;
    }
    return config;
};

/**
 * Adds authentication headers to the request config.
 * @param {Object} config - The Axios request configuration.
 * @returns {Promise<Object>} The updated Axios request configuration.
 */
const addAuthHeaders = async config => {
    const impersonationToken = localStorage.getItem('impersonation_token');
    const token = impersonationToken ?? localStorage.getItem('access_token');
    if (token) {
        config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
};

/**
 * Axios instance for conversation API requests.
 * Sets up a base URL and common headers.
 * @type {import("axios").AxiosInstance}
 */
export const conversationApiHttp = axios.create({
    baseURL:
        process.env.REACT_APP_RUNNING_BACKEND_LOCALLY === 'true'
            ? process.env.REACT_APP_CONVERSATION_API_URL
            : process.env.REACT_APP_API_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

/**
 * Request interceptor for conversationApiHttp instance.
 * Automatically adds X-Tenant-Namespace header.
 * @param {Object} config - The Axios request configuration.
 * @returns {Object} The updated Axios request configuration.
 */
conversationApiHttp.interceptors.request.use(
    config => {
        // Add X-Tenant-Namespace header from localStorage if it is not already set
        // and if the skipTenantHeader flag is not true.
        const companyCode = window.localStorage.getItem('companyCode') || null;
        if (
            companyCode &&
            !config.headers['X-Tenant-Namespace'] &&
            !config.skipTenantHeader
        ) {
            config.headers['X-Tenant-Namespace'] = companyCode;
        }

        const impersonationToken = window.localStorage.getItem(
            'impersonation_token',
        );
        const token =
            impersonationToken ?? window.localStorage.getItem('access_token');
        if (token) {
            config.headers['Authorization'] = `Bearer ${token}`;
        }

        return config;
    },
    error => {
        return Promise.reject(error);
    },
);

/**
 * Sets the logout function to be called when a 401 response is received.
 * @param {Function} logoutFn - The logout function.
 */
export const setLogoutFunction = logoutFn => {
    logoutFunction = logoutFn;
};

setupInterceptors();
